/**
 * As primitivas de gráfico do painel.
 *
 * SVG à mão, sem biblioteca: o painel precisa de três formas (linha, coluna e grade de
 * intensidade) e nenhuma delas justifica 60 KB de runtime no bundle de uma SPA que já carrega
 * React, tRPC e o design system. Mais importante que o tamanho: as regras que este produto
 * tem sobre gráfico — `0` diferente de "sem medição", cor que não muda com filtro, toda
 * série com tabela equivalente — são regras que uma biblioteca genérica não conhece.
 *
 * **Nada aqui calcula métrica** (invariante 3). Os componentes recebem números prontos do
 * servidor e só escolhem coordenada e cor. A única aritmética permitida é a que transforma
 * valor em pixel.
 *
 * ### As regras de desenho que estes componentes seguem
 *
 * - marca fina: linha de 2px, coluna com no máximo 24px e canto de 4px só na ponta do dado;
 * - grade horizontal discreta, sólida (nunca tracejada) e no máximo 4 marcações no eixo Y;
 * - legenda **sempre** quando há duas séries ou mais; nenhuma quando há uma só (o título já
 *   diz o que está plotado);
 * - rótulo direto **seletivo** — na ponta da linha, nunca um número em cada ponto;
 * - texto nunca veste a cor da série: quem carrega identidade é a marca colorida ao lado;
 * - todo gráfico tem tabela equivalente (`ChartCard` liga o botão "Ver tabela"), porque
 *   valor que só existe no gráfico é valor que não existe para quem usa leitor de tela.
 */
import { useId, useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardDescription,
  CardTitle,
  DeltaIndicator,
  cn,
  formatNumber,
  type StatTileDelta,
} from '@mgdlead/ui';

import './charts.css';

/* ========================================================================== */
/*  Moldura                                                                    */
/* ========================================================================== */

export interface ChartCardProps {
  title: string;
  description?: string;
  /** A legenda, quando há duas séries ou mais. */
  legend?: React.ReactNode;
  /** O gráfico. */
  children: React.ReactNode;
  /** A MESMA informação em tabela. Obrigatória — ver o cabeçalho. */
  table: React.ReactNode;
  /** Quando não há o que desenhar: a frase que diz o que falta acontecer. */
  empty?: React.ReactNode;
  /** Nota de rodapé — é onde mora o "este número está definido em metricas.md §X". */
  footnote?: React.ReactNode;
  /** Controle extra no cabeçalho, à esquerda do "Ver tabela" (ex.: escolher a série). */
  action?: React.ReactNode;
  /**
   * G2 (07/10): variação do total do gráfico contra o período anterior, pronta do servidor.
   * `value: null` = sem base → "—" com o porquê no tooltip. Ausente = gráfico sem comparação.
   */
  delta?: StatTileDelta;
}

/**
 * A moldura de todo gráfico: título, legenda, o desenho e o botão que troca o desenho pela
 * tabela.
 *
 * O botão não é enfeite de acessibilidade: é o caminho para ler um valor exato sem depender
 * de acertar o mouse em cima de um ponto de 8px, e é a única leitura possível para quem usa
 * leitor de tela. Por isso ele fica no cabeçalho do cartão, visível, e não escondido atrás de
 * um ícone.
 */
export function ChartCard({
  title,
  description,
  legend,
  children,
  table,
  empty,
  footnote,
  action,
  delta,
}: ChartCardProps) {
  const [mostrarTabela, setMostrarTabela] = useState(false);
  const painelId = useId();

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{title}</CardTitle>
            {delta && !empty ? <DeltaIndicator {...delta} /> : null}
          </div>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {action && !mostrarTabela && !empty ? action : null}
          <Button
            size="sm"
            onClick={() => setMostrarTabela((v) => !v)}
            aria-expanded={mostrarTabela}
            aria-controls={painelId}
          >
            {mostrarTabela ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        </div>
      </div>

      {legend && !mostrarTabela ? <div className="mt-3">{legend}</div> : null}

      <div id={painelId} className="mt-4">
        {empty ? empty : mostrarTabela ? table : children}
      </div>

      {footnote ? (
        <p className="mt-4 border-t border-border pt-3 text-xs text-subtle-foreground">
          {footnote}
        </p>
      ) : null}
    </Card>
  );
}

/** Uma entrada de legenda: marca colorida + rótulo em token de TEXTO, nunca na cor da série. */
export function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span
        aria-hidden="true"
        className="inline-block size-2.5 shrink-0 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

/**
 * A hachura de "sem medição".
 *
 * Um trecho hachurado, e não uma linha caindo a zero: a linha em zero afirmaria "ninguém
 * entrou", quando o que houve foi "não estávamos olhando". É a invariante 8 desenhada.
 */
function HatchDefs({ id }: { id: string }) {
  return (
    <defs>
      <pattern
        id={id}
        width="6"
        height="6"
        patternTransform="rotate(45)"
        patternUnits="userSpaceOnUse"
      >
        <rect width="6" height="6" fill="var(--color-muted)" />
        <line x1="0" y1="0" x2="0" y2="6" stroke="var(--color-border-strong)" strokeWidth="1.5" />
      </pattern>
    </defs>
  );
}

/* ========================================================================== */
/*  Medida real — o gráfico desenha em PIXEL, não em unidade escalada          */
/* ========================================================================== */

/** Largura de fallback quando ainda não há layout (primeiro render, jsdom no teste). */
const LARGURA_PADRAO = 560;

/**
 * A largura real do container, em pixels.
 *
 * Existe para o SVG ser desenhado **no tamanho em que vai ser lido**, em vez de ter um
 * `viewBox` fixo esticado pelo CSS. A diferença não é estética: com `viewBox` de 600 dentro de
 * um cartão de 330px no celular, tudo é multiplicado por 0,55 — e um rótulo de eixo de 9px
 * chega ao olho com **5px**. O gráfico fica bonito no desktop e ilegível justamente na tela em
 * que o dono abre o painel.
 *
 * Desenhando 1:1, 10px é 10px nos dois lugares, e a linha de 2px é 2px nos dois.
 */
function useLarguraDoPai(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [largura, setLargura] = useState(LARGURA_PADRAO);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => {
      const w = el.clientWidth;
      // `0` acontece em container escondido e no jsdom: manter o padrão é melhor que
      // desenhar um gráfico de largura zero, que vira um cartão vazio sem explicação.
      if (w > 0) setLargura(w);
    };
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, largura];
}

/* ========================================================================== */
/*  Escala                                                                     */
/* ========================================================================== */

/**
 * Até onde o eixo Y vai, e quais marcações ele mostra.
 *
 * **As marcações são números redondos de verdade.** A versão anterior dividia o topo em
 * quatro partes iguais e formatava sem casa decimal: com o topo em 5, os cortes caíam em
 * 1,25 / 2,5 / 3,75 e a tela mostrava `0 1 3 4 5` — um eixo que pula o 2 e cuja linha do "1"
 * está, na verdade, em 1,25. Um eixo que mente sobre a própria posição é pior que nenhum.
 *
 * O passo sai de `1, 2, 5 × 10^k`, que é o conjunto que produz número redondo em qualquer
 * escala, e é escolhido o menor que cabe em até 4 divisões (o teto do DESIGN_SYSTEM §7).
 *
 * Teto mínimo `1`: com todos os valores em zero, um domínio `[0, 0]` faria toda coordenada
 * virar `NaN` e o gráfico sumir — que é como um dia legitimamente parado viraria
 * "o gráfico quebrou".
 */
export function escalaY(max: number): { top: number; ticks: number[] } {
  if (max <= 0) return { top: 1, ticks: [0, 1] };

  const MAX_DIVISOES = 4;
  const grandeza = Math.pow(10, Math.floor(Math.log10(max / MAX_DIVISOES)));
  let passo = grandeza;
  for (const mult of [1, 2, 5, 10]) {
    passo = grandeza * mult;
    if (max / passo <= MAX_DIVISOES) break;
  }

  const divisoes = Math.max(1, Math.ceil(max / passo));
  const top = passo * divisoes;
  const ticks: number[] = [];
  for (let i = 0; i <= divisoes; i += 1) ticks.push(passo * i);
  return { top, ticks };
}

/* ========================================================================== */
/*  Linha — série temporal com duas séries                                     */
/* ========================================================================== */

export interface LineSeries {
  id: string;
  label: string;
  color: string;
  /** Um valor por ponto do eixo X, na mesma ordem. */
  values: number[];
}

export interface LineChartProps {
  /** Os rótulos do eixo X, na ordem. */
  labels: string[];
  /** Quais índices foram MEDIDOS. Os outros viram hachura, não zero. */
  measured: boolean[];
  series: LineSeries[];
  /** Descrição curta do que o gráfico mostra, para leitor de tela. */
  ariaLabel: string;
  height?: number;
}

const LINE_PADDING = { top: 10, right: 44, bottom: 20, left: 34 };

export function LineChart({ labels, measured, series, ariaLabel, height = 180 }: LineChartProps) {
  const hatchId = useId();
  const [ref, largura] = useLarguraDoPai();
  const { top, ticks } = escalaY(Math.max(1, ...series.flatMap((s) => s.values)));

  const plotW = largura - LINE_PADDING.left - LINE_PADDING.right;
  const plotH = height - LINE_PADDING.top - LINE_PADDING.bottom;
  const n = labels.length;

  const x = (i: number) => LINE_PADDING.left + (n <= 1 ? plotW / 2 : (plotW * i) / (n - 1));
  const y = (v: number) => LINE_PADDING.top + plotH - (plotH * v) / top;

  // O primeiro índice medido: antes dele o gráfico não desenha linha nenhuma.
  const inicio = measured.indexOf(true);
  const temMedicao = inicio >= 0;

  return (
    <div ref={ref} className="w-full">
      <svg
        viewBox={`0 0 ${largura} ${height}`}
        width={largura}
        height={height}
        className="block max-w-full"
        role="img"
        aria-label={ariaLabel}
      >
        <HatchDefs id={hatchId} />

        {/* Faixa de "sem medição" — antes do primeiro evento do workspace. */}
        {temMedicao && inicio > 0 ? (
          <rect
            x={LINE_PADDING.left}
            y={LINE_PADDING.top}
            width={Math.max(0, x(inicio) - LINE_PADDING.left)}
            height={plotH}
            fill={`url(#${hatchId})`}
            opacity={0.55}
          />
        ) : null}

        {/* Grade horizontal: hairline SÓLIDA, um passo fora da superfície, recessiva. */}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={LINE_PADDING.left}
              x2={largura - LINE_PADDING.right}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--color-border)"
              strokeWidth="1"
            />
            <text
              x={LINE_PADDING.left - 6}
              y={y(t) + 3}
              textAnchor="end"
              className="nums fill-muted-foreground text-2xs"
            >
              {formatNumber(t)}
            </text>
          </g>
        ))}

        {temMedicao
          ? series.map((s) => {
              const pontos = s.values
                .map((v, i) => (measured[i] ? `${x(i)},${y(v)}` : null))
                .filter((p): p is string => p !== null)
                .join(' ');
              const ultimo = s.values.length - 1;
              return (
                <g key={s.id}>
                  <polyline
                    points={pontos}
                    fill="none"
                    stroke={s.color}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {/* Ponto final com anel na cor da superfície, para não sumir num cruzamento. */}
                  <circle
                    cx={x(ultimo)}
                    cy={y(s.values[ultimo] ?? 0)}
                    r="4"
                    fill={s.color}
                    stroke="var(--color-surface)"
                    strokeWidth="2"
                  />
                  {/*
                  Rótulo direto SÓ na ponta — nunca um número em cada ponto. E em token de
                  texto, não na cor da série: âmbar claro sobre fundo claro é ilegível.
                */}
                  <text
                    x={x(ultimo) + 8}
                    y={y(s.values[ultimo] ?? 0) + 3}
                    className="nums fill-foreground text-2xs font-medium"
                  >
                    {formatNumber(s.values[ultimo] ?? 0)}
                  </text>
                </g>
              );
            })
          : null}

        {/* Eixo X: só as pontas. 30 datas empilhadas viram borrão. */}
        <text x={LINE_PADDING.left} y={height - 6} className="nums fill-muted-foreground text-2xs">
          {labels[0]}
        </text>
        <text
          x={largura - LINE_PADDING.right}
          y={height - 6}
          textAnchor="end"
          className="nums fill-muted-foreground text-2xs"
        >
          {labels[n - 1]}
        </text>
      </svg>
    </div>
  );
}

/* ========================================================================== */
/*  Coluna — distribuição por hora                                             */
/* ========================================================================== */

export interface ColumnChartProps {
  labels: string[];
  values: number[];
  color: string;
  ariaLabel: string;
  /** Índices a destacar com rótulo direto (os picos). Nunca todos. */
  highlight?: number[];
  height?: number;
}

const COLUMN_PADDING = { top: 14, right: 6, bottom: 18, left: 30 };

export function ColumnChart({
  labels,
  values,
  color,
  ariaLabel,
  highlight = [],
  height = 150,
}: ColumnChartProps) {
  const [ref, largura] = useLarguraDoPai();
  const { top, ticks } = escalaY(Math.max(...values, 0));

  const plotW = largura - COLUMN_PADDING.left - COLUMN_PADDING.right;
  const plotH = height - COLUMN_PADDING.top - COLUMN_PADDING.bottom;
  const faixa = plotW / values.length;
  /** Vão de 2px na cor da superfície separando colunas vizinhas — nunca um contorno. */
  const larguraBarra = Math.max(2, Math.min(24, faixa - 2));

  return (
    <div ref={ref} className="w-full">
      <svg
        viewBox={`0 0 ${largura} ${height}`}
        width={largura}
        height={height}
        className="block max-w-full"
        role="img"
        aria-label={ariaLabel}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={COLUMN_PADDING.left}
              x2={largura - COLUMN_PADDING.right}
              y1={COLUMN_PADDING.top + plotH - (plotH * t) / top}
              y2={COLUMN_PADDING.top + plotH - (plotH * t) / top}
              stroke="var(--color-border)"
              strokeWidth="1"
            />
            <text
              x={COLUMN_PADDING.left - 5}
              y={COLUMN_PADDING.top + plotH - (plotH * t) / top + 3}
              textAnchor="end"
              className="nums fill-muted-foreground text-2xs"
            >
              {formatNumber(t)}
            </text>
          </g>
        ))}

        {values.map((v, i) => {
          const alturaBarra = (plotH * v) / top;
          const cx = COLUMN_PADDING.left + faixa * i + faixa / 2;
          return (
            <g key={labels[i] ?? i}>
              {v > 0 ? (
                <rect
                  x={cx - larguraBarra / 2}
                  y={COLUMN_PADDING.top + plotH - alturaBarra}
                  width={larguraBarra}
                  height={alturaBarra}
                  // Canto arredondado SÓ na ponta do dado; a base fica quadrada na linha zero.
                  rx={Math.min(4, larguraBarra / 2)}
                  fill={color}
                >
                  <title>{`${labels[i]}: ${formatNumber(v)}`}</title>
                </rect>
              ) : null}
              {highlight.includes(i) && v > 0 ? (
                <text
                  x={cx}
                  y={COLUMN_PADDING.top + plotH - alturaBarra - 4}
                  textAnchor="middle"
                  className="nums fill-foreground text-2xs font-medium"
                >
                  {formatNumber(v)}
                </text>
              ) : null}
              {i % 3 === 0 ? (
                <text
                  x={cx}
                  y={height - 5}
                  textAnchor="middle"
                  className="nums fill-muted-foreground text-2xs"
                >
                  {labels[i]}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ========================================================================== */
/*  Grade de intensidade — atividade anual                                     */
/* ========================================================================== */

export interface ActivityCell {
  date: string;
  intensity: number;
  /** `0` = medido e parado; `1..5` = faixa de percentil; `null` = sem medição. */
  level: number | null;
}

/**
 * A atividade do ano em **12 linhas (mês) × 31 colunas (dia)**.
 *
 * Não é a grade de quadrados semana-a-semana do GitHub, e isso é decisão de produto
 * (`DESIGN_SYSTEM.md` §7 proíbe copiá-la). Mês × dia tem duas vantagens concretas aqui: cabe
 * em 390px sem rolagem horizontal (31 colunas, não 53) e responde a pergunta que o dono faz
 * de verdade — "que dia do mês a operação se mexe?".
 *
 * Três estados de célula, e os três são diferentes de propósito:
 *  - **sem medição** (`level === null`): hachura. Não estávamos olhando.
 *  - **medido e parado** (`level === 0`): superfície com borda. Olhamos, e não houve nada.
 *  - **atividade** (`1..5`): a rampa sequencial validada.
 */
export function ActivityGrid({ cells }: { cells: readonly ActivityCell[] }) {
  const porMes = new Map<string, Map<number, ActivityCell>>();
  for (const c of cells) {
    const mes = c.date.slice(0, 7);
    const dia = Number(c.date.slice(8, 10));
    const linha = porMes.get(mes) ?? new Map<number, ActivityCell>();
    linha.set(dia, c);
    porMes.set(mes, linha);
  }
  const meses = [...porMes.keys()].sort();

  return (
    <div data-mgd-ramp className="w-full">
      <table className="w-full table-fixed border-separate border-spacing-px">
        <caption className="sr-only">
          Atividade diária dos últimos 12 meses. Cada célula é um dia; a intensidade da cor segue a
          faixa de percentil.
        </caption>
        <tbody>
          {meses.map((mes) => (
            <tr key={mes}>
              <th
                scope="row"
                className="w-9 pr-1 text-right align-middle text-2xs font-normal text-muted-foreground"
              >
                {rotuloMes(mes)}
              </th>
              {Array.from({ length: 31 }, (_, i) => {
                const cell = porMes.get(mes)?.get(i + 1);
                return (
                  <td key={i} className="p-0">
                    <ActivityCellBox cell={cell} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityCellBox({ cell }: { cell: ActivityCell | undefined }) {
  // Dia que não existe naquele mês (31 de fevereiro). Ausente não é "sem dado".
  if (!cell) return <span aria-hidden="true" className="block aspect-square rounded-xs" />;

  if (cell.level === null) {
    return (
      <span
        title={`${cell.date}: sem medição`}
        className="block aspect-square rounded-xs bg-muted bg-hatch"
      />
    );
  }
  if (cell.level === 0) {
    return (
      <span
        title={`${cell.date}: nenhuma movimentação`}
        className="block aspect-square rounded-xs border border-border bg-surface"
      />
    );
  }
  return (
    <span
      title={`${cell.date}: ${cell.intensity} movimentações`}
      className="block aspect-square rounded-xs"
      style={{ backgroundColor: `var(--ramp-${cell.level})` }}
    />
  );
}

/** A legenda da rampa. Sem ela, a cor é uma escala que ninguém sabe ler. */
export function RampLegend({ peak }: { peak: number | null }) {
  return (
    <div
      data-mgd-ramp
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground"
    >
      <span className="inline-flex items-center gap-1">
        <span aria-hidden="true" className="size-3 rounded-xs bg-muted bg-hatch" />
        sem medição
      </span>
      <span className="inline-flex items-center gap-1">
        <span aria-hidden="true" className="size-3 rounded-xs border border-border bg-surface" />
        nada
      </span>
      <span className="inline-flex items-center gap-1">
        menos
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            aria-hidden="true"
            className="size-3 rounded-xs"
            style={{ backgroundColor: `var(--ramp-${n})` }}
          />
        ))}
        mais
      </span>
      {peak !== null ? <span className="nums">pico: {formatNumber(peak)} no dia</span> : null}
    </div>
  );
}

/** Célula de tabela tingida pela rampa — a mesma escala do mapa, usada na tabela de coortes. */
export function RampCell({
  level,
  children,
  className,
}: {
  level: number | null;
  children: React.ReactNode;
  className?: string;
}) {
  if (level === null || level === 0) {
    return <span className={cn('nums block text-muted-foreground', className)}>{children}</span>;
  }
  return (
    <span
      data-mgd-ramp
      className={cn('nums block rounded-sm px-1.5 py-0.5 text-center font-medium', className)}
      style={{
        backgroundColor: `var(--ramp-${level})`,
        // Tinta escolhida pela luminância do preenchimento — a exceção à regra de "texto não
        // veste a cor do dado" é justamente o texto DENTRO do preenchimento.
        color: level >= 3 ? 'var(--ramp-ink-dark)' : 'var(--ramp-ink-light)',
      }}
    >
      {children}
    </span>
  );
}

const MESES = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

function rotuloMes(mes: string): string {
  const [ano = '', m = '01'] = mes.split('-');
  return `${MESES[Number(m) - 1] ?? m}/${ano.slice(2)}`;
}
